import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cachedPatientService } from '../services/cachedApiService';
import { PatientDetailResponse } from '../types';
import Layout from '../components/Layout';
import '../styles/PatientDetail.css';

const PatientDetail: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const [patientData, setPatientData] = useState<PatientDetailResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPatientDetail = async () => {
      if (!patientId) return;
      
      try {
        setLoading(true);
        const data = await cachedPatientService.getPatientDetail(patientId);
        setPatientData(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch patient details');
        setLoading(false);
      }
    };

    fetchPatientDetail();
  }, [patientId]);

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading patient details...</div>
        </div>
      </Layout>
    );
  }

  if (error || !patientData) {
    return (
      <Layout>
        <div className="error">{error || 'Patient not found'}</div>
      </Layout>
    );
  }

  const { patient, insurance, problems, allergies, encounters, prescriptions, prior_authorizations } = patientData;

  return (
    <Layout>
      <div className="container">
        <div className="card mb-4 fade-in">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div className="avatar avatar-lg" style={{ backgroundColor: '#4299e1' }}>
                {patient.fname.charAt(0)}{patient.lname.charAt(0)}
              </div>
              <div>
                <h2 className="mb-0">{patient.fname} {patient.lname}</h2>
                <div className="text-light">Patient ID: {patient.pid}</div>
              </div>
            </div>
            <Link to={`/raise-pa/${patient.pid}`} className="btn btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Raise PA
            </Link>
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Personal Information
            </h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col mb-3">
                <div className="d-flex">
                  <div style={{ width: '150px', fontWeight: '500', color: 'var(--text-light)' }}>Date of Birth:</div>
                  <div>{patient.dob}</div>
                </div>
              </div>
              <div className="col mb-3">
                <div className="d-flex">
                  <div style={{ width: '150px', fontWeight: '500', color: 'var(--text-light)' }}>Gender:</div>
                  <div>{patient.gender}</div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col mb-3">
                <div className="d-flex">
                  <div style={{ width: '150px', fontWeight: '500', color: 'var(--text-light)' }}>Phone:</div>
                  <div>{patient.phone}</div>
                </div>
              </div>
              <div className="col mb-3">
                <div className="d-flex">
                  <div style={{ width: '150px', fontWeight: '500', color: 'var(--text-light)' }}>Email:</div>
                  <div>{patient.email}</div>
                </div>
              </div>
            </div>
            {patient.address && (
              <div className="row">
                <div className="col">
                  <div className="d-flex">
                    <div style={{ width: '150px', fontWeight: '500', color: 'var(--text-light)' }}>Address:</div>
                    <div>{patient.address}, {patient.city}, {patient.state} {patient.zip}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M1 10H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Insurance Information
            </h3>
          </div>
          <div className="card-body">
            {insurance.length > 0 ? (
              <div className="row">
                {insurance.map((ins) => (
                  <div key={ins.id} className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">{ins.provider} ({ins.type})</h4>
                        {ins.ePA && <span className="status-badge success">ePA Enabled</span>}
                      </div>
                      <div className="card-body">
                        <div className="mb-2">
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Plan:</div>
                          <div>{ins.plan_name}</div>
                        </div>
                        <div className="mb-2">
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Policy #:</div>
                          <div>{ins.policy_number}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Group #:</div>
                          <div>{ins.group_number}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center p-3" style={{ color: 'var(--text-light)' }}>No insurance information available</p>
            )}
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Medical Problems
            </h3>
          </div>
          <div className="card-body">
            {problems.length > 0 ? (
              <div className="row">
                {problems.map((problem) => (
                  <div key={problem.id} className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">{problem.title}</h4>
                        <span className="badge badge-primary">{problem.severity}</span>
                      </div>
                      <div className="card-body">
                        <div className="mb-2">
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Diagnosis Code:</div>
                          <div>{problem.diagnosis_code}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Date:</div>
                          <div>{problem.date}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center p-3" style={{ color: 'var(--text-light)' }}>No medical problems recorded</p>
            )}
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Allergies
            </h3>
          </div>
          <div className="card-body">
            {allergies.length > 0 ? (
              <div className="row">
                {allergies.map((allergy) => (
                  <div key={allergy.id} className="col-md-4 mb-3">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">{allergy.allergen}</h4>
                        <span className="badge badge-danger">{allergy.severity}</span>
                      </div>
                      <div className="card-body">
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Reaction:</div>
                          <div>{allergy.reaction}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center p-3" style={{ color: 'var(--text-light)' }}>No allergies recorded</p>
            )}
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Encounters & Procedures
            </h3>
          </div>
          <div className="card-body">
            {encounters.length > 0 ? (
              <div className="row">
                {encounters.map((encounter) => (
                  <div key={encounter.encounter.id} className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-header">
                        <h4 className="mb-0">Encounter on {encounter.encounter.date}</h4>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Reason:</div>
                          <div>{encounter.encounter.reason}</div>
                        </div>
                        
                        {encounter.procedures.length > 0 && (
                          <div>
                            <h5 className="mb-2">Procedures</h5>
                            <ul className="p-0" style={{ listStyle: 'none' }}>
                              {encounter.procedures.map((procedure) => (
                                <li key={procedure.id} className="mb-2 p-2" style={{ backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <strong>{procedure.procedure_name}</strong>
                                    {procedure.PA_required && (
                                      <span className="status-badge warning">PA Required</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                                    Code: {procedure.procedure_code} | Scheduled: {procedure.scheduled_date}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center p-3" style={{ color: 'var(--text-light)' }}>No encounters recorded</p>
            )}
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H15M9 15H15M9 7H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Prescriptions
            </h3>
          </div>
          <div className="card-body">
            {prescriptions.length > 0 ? (
              <div className="row">
                {prescriptions.map((prescription) => (
                  <div key={prescription.id} className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">{prescription.drug_name}</h4>
                        {prescription.PA_required && (
                          <span className="status-badge warning">PA Required</span>
                        )}
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Dosage:</div>
                            <div>{prescription.dosage}</div>
                          </div>
                          <div className="col-md-6 mb-2">
                            <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Quantity:</div>
                            <div>{prescription.quantity}</div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Frequency:</div>
                            <div>{prescription.frequency}</div>
                          </div>
                          <div className="col-md-6 mb-2">
                            <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Duration:</div>
                            <div>{prescription.duration}</div>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Pharmacy:</div>
                          <div>{prescription.pharmacy}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center p-3" style={{ color: 'var(--text-light)' }}>No prescriptions recorded</p>
            )}
          </div>
        </div>

        <div className="card mb-4 fade-in">
          <div className="card-header">
            <h3 className="card-title d-flex align-items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Prior Authorizations
            </h3>
          </div>
          <div className="card-body">
            {prior_authorizations.length > 0 ? (
              <div className="row">
                {prior_authorizations.map((pa) => (
                  <div key={pa.id} className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">PA #{pa.id} - {pa.request_type}</h4>
                        <span className={`status-badge ${pa.urgency === 'urgent' ? 'warning' : 'info'}`}>
                          {pa.urgency}
                        </span>
                      </div>
                      <div className="card-body">
                        <div className="mb-2">
                          <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Created:</div>
                          <div>{pa.created_at}</div>
                        </div>
                        {pa.clinical_reason && (
                          <div>
                            <div style={{ fontWeight: '500', color: 'var(--text-light)' }}>Clinical Reason:</div>
                            <div>{pa.clinical_reason}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center p-3" style={{ color: 'var(--text-light)' }}>No prior authorizations recorded</p>
            )}
          </div>
        </div>

        {/* <div className="back-btn mt-4 mb-5">
          <Link to="/patients">
            <button className="d-flex align-items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Patient List
            </button>
          </Link>
        </div> */}
      </div>
    </Layout>
  );
};

export default PatientDetail;